import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  Avatar,
  Typography,
  IconButton,
  Popover,
  Button,
  Tooltip,
  Badge,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  MenuItem,
  Alert,
  Snackbar,
  Divider,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import LockResetIcon from "@mui/icons-material/LockReset";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import DashboardIcon from "@mui/icons-material/Dashboard";
import InfoIcon from "@mui/icons-material/Info";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import PersonIcon from "@mui/icons-material/Person";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import HomeIcon from "@mui/icons-material/Home";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import PhoneMissedIcon from "@mui/icons-material/PhoneMissed";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import VoicemailIcon from "@mui/icons-material/Voicemail";
import axios from "axios";
import MediaFileControl from "./MediaFileControl";
import { application } from "globals/endpoints";
import AuthenticationService from "application/modules/authentication/AuthenticationService";

const MAX_AVATAR_SIZE = 200 * 1024;

const p = (isDark) => ({
  bg: isDark ? "#1e1e1e" : "#ffffff",
  bgHover: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
  bgSubtle: isDark ? "#252525" : "#f5f5f5",
  text: isDark ? "#ffffff" : "rgba(0,0,0,0.87)",
  textSecondary: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
  divider: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
  border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
  avatarBg: isDark ? "#42a5f5" : "#1976d2",
  cameraBg: isDark ? "#2d2d2d" : "#ffffff",
  cameraHover: isDark ? "#424242" : "#f5f5f5",
});

const ProfilePopover = ({
  fullName,
  username,
  description,
  isDark,
  onToggleTheme,
  onChangePassword,
  onLogout,
  showAdmin,
  showUserApp,
  avatarSrc,
  onAvatarUpload,
  avatarUploading,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const fileInputRef = useRef(null);
  const open = Boolean(anchorEl);
  const c = p(isDark);

  const [profileOpen, setProfileOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSnackbar, setProfileSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Dialing features dialog state
  const [dialingOpen, setDialingOpen] = useState(false);
  const [dialingData, setDialingData] = useState(null);
  const [dialingLoading, setDialingLoading] = useState(false);
  const [dialingSaving, setDialingSaving] = useState(false);

  const isMeaningful = (v) => v && v !== "null" && v !== "undefined";
  const displayName =
    (isMeaningful(description) && description) ||
    (isMeaningful(fullName) && fullName) ||
    username ||
    "User";
  const initials = displayName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  const extension = AuthenticationService.getExtensionNumber();

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleFileSelect = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      if (!file || !file.type.startsWith("image/")) return;
      if (file.size > MAX_AVATAR_SIZE) { alert("Image must be under 200KB"); return; }
      const reader = new FileReader();
      reader.onload = (e) => onAvatarUpload?.(e.target.result);
      reader.readAsDataURL(file);
      event.target.value = "";
    },
    [onAvatarUpload],
  );

  const handleOpenProfileDialog = async () => {
    handleClose();
    setProfileOpen(true);
    setProfileLoading(true);
    try {
      const response = await application.post("/user/username/" + username);
      const data = response.data?.data || response.data;
      setProfileData({
        id: data.id, username: data.username || "", contactNo: data.contactNo || "",
        description: data.description || "", emailId: data.emailId || "",
        ipAddress: data.ipAddress || "",
        enabled: data.enabled === true || data.enabled === 1,
        publicEmergencyNumber: data.publicEmergencyNumber || "",
        broadcastNumber: data.broadcastNumber || "",
        dispatcherAutoAnswer: data.dispatcherAutoAnswer || false,
        ringAnswerCount: data.ringAnswerCount || 0,
      });
    } catch (err) {
      setProfileSnackbar({ open: true, message: "Failed to load profile", severity: "error" });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleProfileChange = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setProfileData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "dispatcherAutoAnswer" && !value) updated.ringAnswerCount = 0;
      return updated;
    });
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    try {
      const { id, username, contactNo, ...profilePayload } = profileData;
      await application.post("/user/profile", profilePayload);
      const h = window.location.hostname;
      window.sessionStorage.setItem(`${h}-coral-X-Answer-Ring-Count`, profileData.dispatcherAutoAnswer ? String(profileData.ringAnswerCount || 0) : "0");
      window.sessionStorage.setItem(`${h}-coral-X-Public-Emergency-Number`, profileData.publicEmergencyNumber || "");
      window.sessionStorage.setItem(`${h}-coral-X-Broadcast-Number`, profileData.broadcastNumber || "");
      setProfileSnackbar({ open: true, message: "Profile updated successfully", severity: "success" });
      setProfileOpen(false);
    } catch (err) {
      setProfileSnackbar({ open: true, message: "Failed to save profile", severity: "error" });
    } finally {
      setProfileSaving(false);
    }
  };

  // Fetch dialing features when dialog opens
  const handleOpenDialing = async () => {
    handleClose();
    setDialingOpen(true);
    setDialingLoading(true);
    try {
      const response = await application.post("/user/myExtensionServices");
      const services = response.data?.data || [];
      const state = { cfaEnabled: false, cfaNumber: "", cfbEnabled: false, cfbNumber: "", cfnaEnabled: false, cfnaNumber: "", cfnaRings: 3, ecfaEnabled: false, ecfaNumber: "", ecfbEnabled: false, ecfbNumber: "", ecfnaEnabled: false, ecfnaNumber: "", dndEnabled: false, fmEnabled: false, fmNumber: "", vmEnabled: false, vmGreeting: "" };
      for (const svc of services) {
        switch (svc.serviceCode) {
          case "CFA": state.cfaEnabled = true; state.cfaNumber = svc.data || ""; break;
          case "ECFA": state.ecfaEnabled = true; state.ecfaNumber = svc.data || ""; break;
          case "CFB": state.cfbEnabled = true; state.cfbNumber = svc.data || ""; break;
          case "ECFB": state.ecfbEnabled = true; state.ecfbNumber = svc.data || ""; break;
          case "CFNA": state.cfnaEnabled = true; state.cfnaNumber = svc.data || ""; break;
          case "ECFNA": state.ecfnaEnabled = true; state.ecfnaNumber = svc.data || ""; break;
          case "DND": state.dndEnabled = true; break;
          case "FM": state.fmEnabled = true; state.fmNumber = svc.data || ""; break;
          case "VM_GREETING": state.vmEnabled = true; state.vmGreeting = svc.data || ""; break;
          case "CFNA_RINGS": state.cfnaRings = parseInt(svc.data, 10) || 3; break;
          default: break;
        }
      }
      setDialingData(state);
    } catch (err) {
      console.error("[ProfilePopover] Failed to load dialing features:", err?.message);
      setProfileSnackbar({ open: true, message: "Failed to load dialing features", severity: "error" });
      setDialingOpen(false);
    } finally {
      setDialingLoading(false);
    }
  };

  const handleDialingChange = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setDialingData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "cfaEnabled" && value) {
        updated.cfbEnabled = false; updated.cfbNumber = "";
        updated.cfnaEnabled = false; updated.cfnaNumber = "";
      }
      if (field === "ecfaEnabled" && value) {
        updated.ecfbEnabled = false; updated.ecfbNumber = "";
        updated.ecfnaEnabled = false; updated.ecfnaNumber = "";
      }
      return updated;
    });
  };

  const handleSaveDialing = async () => {
    setDialingSaving(true);
    try {
      const payload = {
        cfa: dialingData.cfaEnabled ? (dialingData.cfaNumber || "") : null,
        ecfa: dialingData.ecfaEnabled ? (dialingData.ecfaNumber || "") : null,
        cfb: dialingData.cfbEnabled ? (dialingData.cfbNumber || "") : null,
        ecfb: dialingData.ecfbEnabled ? (dialingData.ecfbNumber || "") : null,
        cfna: dialingData.cfnaEnabled ? (dialingData.cfnaNumber || "") : null,
        cFnaTime: dialingData.cfnaEnabled ? String(dialingData.cfnaRings || 3) : null,
        ecfna: dialingData.ecfnaEnabled ? (dialingData.ecfnaNumber || "") : null,
        dnd: dialingData.dndEnabled ? "true" : null,
        fm: dialingData.fmEnabled ? (dialingData.fmNumber || "") : null,
        vmGreeting: dialingData.vmEnabled ? (dialingData.vmGreeting || "") : null,
      };
      await application.post("/user/updateMyExtensionServices", payload);
      setProfileSnackbar({ open: true, message: "Dialing features updated", severity: "success" });
      setDialingOpen(false);
    } catch (err) {
      console.error("Failed to save dialing features:", err);
      setProfileSnackbar({ open: true, message: "Failed to save dialing features", severity: "error" });
    } finally {
      setDialingSaving(false);
    }
  };

  const menuItems = [
    { icon: <PersonIcon />, label: "My Profile", onClick: handleOpenProfileDialog, show: true },
    { icon: <PhoneForwardedIcon />, label: "Dialing Features", onClick: handleOpenDialing, show: !!extension },
    { icon: isDark ? <LightModeIcon /> : <DarkModeIcon />, label: isDark ? "Light Mode" : "Dark Mode", onClick: () => { onToggleTheme?.(); handleClose(); }, show: true },
    { icon: <AdminPanelSettingsIcon />, label: "Admin Panel", onClick: () => { window.location.href = window.location.origin + "/admin-gui/"; }, show: showAdmin },
    { icon: <HomeIcon />, label: "User Application", onClick: () => { window.location.href = window.location.origin + "/home-page/"; handleClose(); }, show: showUserApp },
    { icon: <InfoIcon />, label: "About Us", onClick: () => { window.open(window.aboutUsUrl || "https://www.coraltele.com/corporate-mantra", "_blank"); handleClose(); }, show: true },
  ].filter((i) => i.show);

  const borderColor = isDark ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)";
  const tfSx = {
    "& .MuiOutlinedInput-root": {
      color: c.text,
      "& fieldset": { borderColor },
      "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.87)" },
      "&.Mui-focused fieldset": { borderColor: isDark ? "#90caf9" : "#1976d2" },
    },
    "& .MuiInputLabel-root": { color: c.textSecondary },
    "& .MuiInputLabel-root.Mui-focused": { color: isDark ? "#90caf9" : "#1976d2" },
    "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: c.textSecondary },
  };

  return (
    <>
      <Tooltip title={displayName}>
        <IconButton onClick={handleOpen} size="small" sx={{ ml: 0.5 }}>
          <Avatar
            src={avatarSrc || undefined}
            sx={{
              width: 30, height: 30, fontSize: "0.8rem", fontWeight: 600,
              bgcolor: c.avatarBg, color: "#fff", transition: "all 0.2s",
            }}
          >
            {!avatarSrc && initials}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Popover
        open={open} anchorEl={anchorEl} onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 300, mt: 1, borderRadius: "12px", overflow: "hidden",
            bgcolor: c.bg, color: c.text, border: c.border,
            backgroundImage: "none",
            boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.6)" : "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        {/* Header — no gradient, matches dialog style */}
        <Box sx={{ px: 2.5, pt: 2.5, pb: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ position: "relative" }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={
                <IconButton
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={avatarUploading}
                  sx={{ width: 22, height: 22, bgcolor: c.cameraBg, border: `2px solid ${c.avatarBg}`, "&:hover": { bgcolor: c.cameraHover } }}
                >
                  {avatarUploading ? <CircularProgress size={12} /> : <CameraAltIcon sx={{ fontSize: 12, color: isDark ? "#fff" : undefined }} />}
                </IconButton>
              }
            >
              <Avatar
                src={avatarSrc || undefined}
                sx={{ width: 52, height: 52, fontSize: "1.25rem", fontWeight: 700, bgcolor: c.avatarBg, color: "#fff" }}
              >
                {!avatarSrc && initials}
              </Avatar>
            </Badge>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", lineHeight: 1.2, color: c.text }} noWrap>
              {displayName}
            </Typography>
            {username && username !== displayName && (
              <Typography sx={{ fontSize: "0.75rem", color: c.textSecondary }} noWrap>{username}</Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ height: "1px", bgcolor: c.divider }} />

        <Box sx={{ py: 0.5 }}>
          {menuItems.map((item) => (
            <Box
              key={item.label} onClick={item.onClick}
              sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1, cursor: "pointer", "&:hover": { bgcolor: c.bgHover } }}
            >
              {React.cloneElement(item.icon, { sx: { fontSize: 20, color: c.textSecondary } })}
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 400, color: c.text }}>{item.label}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ height: "1px", bgcolor: c.divider }} />

        <Box sx={{ py: 0.5 }}>
          <Box
            onClick={() => { handleClose(); onChangePassword?.(); }}
            sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1, cursor: "pointer", "&:hover": { bgcolor: c.bgHover } }}
          >
            {React.cloneElement(<LockResetIcon />, { sx: { fontSize: 20, color: c.textSecondary } })}
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 400, color: c.text }}>Change Password</Typography>
          </Box>
        </Box>

        <Box sx={{ height: "1px", bgcolor: c.divider }} />

        <Box sx={{ p: 1.5 }}>
          <Button
            fullWidth variant="outlined" size="small" startIcon={<LogoutIcon />}
            onClick={() => { handleClose(); onLogout?.(); }}
            sx={{
              textTransform: "none", borderRadius: 2,
              color: "#d32f2f", borderColor: "#d32f2f",
              "&:hover": { bgcolor: "rgba(211,47,47,0.08)", borderColor: "#c62828" },
            }}
          >
            Sign Out
          </Button>
        </Box>
      </Popover>

      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden onChange={handleFileSelect} />

      {/* Profile Edit Dialog — matches CTLPassword dialog style */}
      <Dialog open={profileOpen} onClose={() => !profileSaving && setProfileOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: c.bg, color: c.text, backgroundImage: "none" } }}
      >
        <DialogTitle sx={{ color: c.text }}>My Profile</DialogTitle>
        <DialogContent>
          {profileLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
          ) : profileData ? (
            <Box component="form" autoComplete="off" sx={{ pt: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField label="User ID" value={profileData.username} disabled fullWidth size="small" variant="outlined" sx={tfSx} autoComplete="off" />
              <TextField label="Contact No. / Extension" value={profileData.contactNo} disabled fullWidth size="small" variant="outlined" sx={tfSx} autoComplete="off" />
              <TextField label="User Name" value={profileData.description} onChange={handleProfileChange("description")} fullWidth size="small" variant="outlined" sx={tfSx} autoComplete="off" />
              <TextField label="Email ID" value={profileData.emailId} onChange={handleProfileChange("emailId")} fullWidth size="small" variant="outlined" sx={tfSx} autoComplete="off" />
              <TextField label="IP Address" value={profileData.ipAddress} onChange={handleProfileChange("ipAddress")} fullWidth size="small" variant="outlined" sx={tfSx} autoComplete="off" />
              <TextField label="Public Emergency Number" value={profileData.publicEmergencyNumber} onChange={handleProfileChange("publicEmergencyNumber")} fullWidth size="small" variant="outlined" inputProps={{ inputMode: "numeric", pattern: "[0-9]*", autoComplete: "off" }} sx={tfSx} autoComplete="off" />
              <TextField label="Broadcast Number" value={profileData.broadcastNumber} onChange={handleProfileChange("broadcastNumber")} fullWidth size="small" variant="outlined" inputProps={{ inputMode: "numeric", pattern: "[0-9]*", autoComplete: "off" }} sx={tfSx} autoComplete="off" />
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <FormControlLabel control={<Switch checked={profileData.enabled} onChange={handleProfileChange("enabled")} size="small" />} label="Active" sx={{ "& .MuiFormControlLabel-label": { color: c.text } }} />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <FormControlLabel control={<Switch checked={profileData.dispatcherAutoAnswer} onChange={handleProfileChange("dispatcherAutoAnswer")} size="small" />} label="Auto Answer" sx={{ "& .MuiFormControlLabel-label": { color: c.text } }} />
              </Box>
              {profileData.dispatcherAutoAnswer && (
                <TextField label="Ring Answer Count" value={profileData.ringAnswerCount} onChange={handleProfileChange("ringAnswerCount")} fullWidth size="small" variant="outlined" select sx={tfSx}>
                  {[1, 2, 3, 4, 5].map((n) => (<MenuItem key={n} value={n}>{n} Ring{n > 1 ? "s" : ""}</MenuItem>))}
                </TextField>
              )}
            </Box>
          ) : (
            <Alert severity="error">Failed to load profile data</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setProfileOpen(false)}
            disabled={profileSaving}
            sx={{ color: isDark ? "rgba(255,255,255,0.7)" : undefined }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveProfile}
            variant="contained"
            disabled={profileSaving || profileLoading || !profileData}
            sx={{
              bgcolor: isDark ? "#66bb6a" : "#2e7d32",
              color: "#fff",
              "&:hover": { bgcolor: isDark ? "#57a05a" : "#1b5e20" },
              "&.Mui-disabled": {
                bgcolor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
                color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.26)",
              },
            }}
          >
            {profileSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialing Features Dialog */}
      <Dialog open={dialingOpen} onClose={() => !dialingSaving && setDialingOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { bgcolor: c.bg, color: c.text, backgroundImage: "none" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1, color: c.text }}>
          <PhoneForwardedIcon color="primary" />
          Dialing Features
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: c.divider }}>
          {dialingLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
          ) : dialingData ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>

              {/* Quick Actions */}
              <Typography variant="subtitle2" sx={{ mt: 0.5, mb: 0.5, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 1, color: c.textSecondary }}>
                Quick Actions
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 0.25, px: 1, borderRadius: 1, bgcolor: dialingData.dndEnabled ? "error.main" : "transparent", "&:hover": { bgcolor: dialingData.dndEnabled ? "error.dark" : c.bgHover } }}>
                <DoNotDisturbIcon sx={{ fontSize: 16, color: dialingData.dndEnabled ? "#fff" : "error.main" }} />
                <FormControlLabel
                  control={<Switch checked={dialingData.dndEnabled} onChange={handleDialingChange("dndEnabled")} size="small" />}
                  label={<Typography variant="body2" sx={{ fontWeight: dialingData.dndEnabled ? 600 : 400, color: dialingData.dndEnabled ? "#fff" : c.text }}>Do Not Disturb</Typography>}
                  sx={{ m: 0 }}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 0.25, px: 1, borderRadius: 1, "&:hover": { bgcolor: c.bgHover } }}>
                <PersonSearchIcon sx={{ fontSize: 16, color: dialingData.fmEnabled ? "primary.main" : c.textSecondary }} />
                <FormControlLabel
                  control={<Switch checked={dialingData.fmEnabled} onChange={handleDialingChange("fmEnabled")} size="small" />}
                  label={<Typography variant="body2" sx={{ color: c.text }}>Follow Me</Typography>}
                  sx={{ m: 0, minWidth: 130 }}
                />
                {dialingData.fmEnabled && (
                  <TextField label="Number" value={dialingData.fmNumber} onChange={handleDialingChange("fmNumber")} size="small" placeholder="Ext. / Number" sx={{ flex: 1, ...tfSx }} />
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 0.25, px: 1, borderRadius: 1, "&:hover": { bgcolor: c.bgHover } }}>
                <VoicemailIcon sx={{ fontSize: 16, color: dialingData.vmEnabled ? "primary.main" : c.textSecondary }} />
                <FormControlLabel
                  control={<Switch checked={dialingData.vmEnabled} onChange={handleDialingChange("vmEnabled")} size="small" />}
                  label={<Typography variant="body2" sx={{ color: c.text }}>VM Greeting</Typography>}
                  sx={{ m: 0, minWidth: 130 }}
                />
                {dialingData.vmEnabled && (
                  <MediaFileControl
                    label="VM Greeting"
                    value={dialingData.vmGreeting}
                    onChange={(path) => setDialingData((prev) => ({ ...prev, vmGreeting: path }))}
                    entityType="extensions"
                    fieldName="vmGreeting"
                    size="small"
                  />
                )}
              </Box>

              <Divider sx={{ my: 1, borderColor: c.divider }} />

              {/* Call Forwarding — two columns */}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>

                {/* Internal */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.25, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 1, color: c.textSecondary }}>
                    Internal Call Forwarding
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5, px: 0.5, color: c.textSecondary }}>
                    Forward to another extension
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 0.25, px: 1, borderRadius: 1, "&:hover": { bgcolor: c.bgHover } }}>
                    <PhoneForwardedIcon sx={{ fontSize: 16, color: dialingData.cfaEnabled ? "warning.main" : c.textSecondary }} />
                    <FormControlLabel
                      control={<Switch checked={dialingData.cfaEnabled} onChange={handleDialingChange("cfaEnabled")} size="small" />}
                      label={<Typography variant="body2" sx={{ color: c.text }}>All Calls</Typography>}
                      sx={{ m: 0, minWidth: 130 }}
                    />
                    {dialingData.cfaEnabled && (
                      <TextField label="Ext." value={dialingData.cfaNumber} onChange={handleDialingChange("cfaNumber")} size="small" sx={{ flex: 1, ...tfSx }} />
                    )}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 0.25, px: 1, borderRadius: 1, opacity: dialingData.cfaEnabled ? 0.5 : 1, "&:hover": { bgcolor: c.bgHover } }}>
                    <PhoneDisabledIcon sx={{ fontSize: 16, color: dialingData.cfbEnabled ? "info.main" : c.textSecondary }} />
                    <FormControlLabel
                      control={<Switch checked={dialingData.cfbEnabled} onChange={handleDialingChange("cfbEnabled")} size="small" disabled={dialingData.cfaEnabled} />}
                      label={<Typography variant="body2" sx={{ color: c.text }}>On Busy</Typography>}
                      sx={{ m: 0, minWidth: 130 }}
                    />
                    {dialingData.cfbEnabled && (
                      <TextField label="Ext." value={dialingData.cfbNumber} onChange={handleDialingChange("cfbNumber")} size="small" sx={{ flex: 1, ...tfSx }} />
                    )}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 0.25, px: 1, borderRadius: 1, opacity: dialingData.cfaEnabled ? 0.5 : 1, "&:hover": { bgcolor: c.bgHover } }}>
                    <PhoneMissedIcon sx={{ fontSize: 16, color: dialingData.cfnaEnabled ? "success.main" : c.textSecondary }} />
                    <FormControlLabel
                      control={<Switch checked={dialingData.cfnaEnabled} onChange={handleDialingChange("cfnaEnabled")} size="small" disabled={dialingData.cfaEnabled} />}
                      label={<Typography variant="body2" sx={{ color: c.text }}>No Answer</Typography>}
                      sx={{ m: 0, minWidth: 130 }}
                    />
                    {dialingData.cfnaEnabled && (
                      <>
                        <TextField label="Ext." value={dialingData.cfnaNumber} onChange={handleDialingChange("cfnaNumber")} size="small" sx={{ flex: 1, ...tfSx }} />
                        <TextField label="Rings" value={dialingData.cfnaRings} onChange={handleDialingChange("cfnaRings")} size="small" select sx={{ width: 72, ...tfSx }}>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                            <MenuItem key={n} value={n}>{n}</MenuItem>
                          ))}
                        </TextField>
                      </>
                    )}
                  </Box>
                </Box>

                {/* External */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.25, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 1, color: c.textSecondary }}>
                    External Call Forwarding
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5, px: 0.5, color: c.textSecondary }}>
                    Forward to an outside number. If not set separately, external calls follow internal forwarding rules.
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 0.25, px: 1, borderRadius: 1, "&:hover": { bgcolor: c.bgHover } }}>
                    <PhoneForwardedIcon sx={{ fontSize: 16, color: dialingData.ecfaEnabled ? "warning.main" : c.textSecondary }} />
                    <FormControlLabel
                      control={<Switch checked={dialingData.ecfaEnabled} onChange={handleDialingChange("ecfaEnabled")} size="small" />}
                      label={<Typography variant="body2" sx={{ color: c.text }}>All Calls</Typography>}
                      sx={{ m: 0, minWidth: 130 }}
                    />
                    {dialingData.ecfaEnabled && (
                      <TextField label="Number" value={dialingData.ecfaNumber} onChange={handleDialingChange("ecfaNumber")} size="small" sx={{ flex: 1, ...tfSx }} />
                    )}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 0.25, px: 1, borderRadius: 1, opacity: dialingData.ecfaEnabled ? 0.5 : 1, "&:hover": { bgcolor: c.bgHover } }}>
                    <PhoneDisabledIcon sx={{ fontSize: 16, color: dialingData.ecfbEnabled ? "info.main" : c.textSecondary }} />
                    <FormControlLabel
                      control={<Switch checked={dialingData.ecfbEnabled} onChange={handleDialingChange("ecfbEnabled")} size="small" disabled={dialingData.ecfaEnabled} />}
                      label={<Typography variant="body2" sx={{ color: c.text }}>On Busy</Typography>}
                      sx={{ m: 0, minWidth: 130 }}
                    />
                    {dialingData.ecfbEnabled && (
                      <TextField label="Number" value={dialingData.ecfbNumber} onChange={handleDialingChange("ecfbNumber")} size="small" sx={{ flex: 1, ...tfSx }} />
                    )}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 0.25, px: 1, borderRadius: 1, opacity: dialingData.ecfaEnabled ? 0.5 : 1, "&:hover": { bgcolor: c.bgHover } }}>
                    <PhoneMissedIcon sx={{ fontSize: 16, color: dialingData.ecfnaEnabled ? "success.main" : c.textSecondary }} />
                    <FormControlLabel
                      control={<Switch checked={dialingData.ecfnaEnabled} onChange={handleDialingChange("ecfnaEnabled")} size="small" disabled={dialingData.ecfaEnabled} />}
                      label={<Typography variant="body2" sx={{ color: c.text }}>No Answer</Typography>}
                      sx={{ m: 0, minWidth: 130 }}
                    />
                    {dialingData.ecfnaEnabled && (
                      <TextField label="Number" value={dialingData.ecfnaNumber} onChange={handleDialingChange("ecfnaNumber")} size="small" sx={{ flex: 1, ...tfSx }} />
                    )}
                  </Box>
                </Box>

              </Box>
            </Box>
          ) : (
            <Alert severity="error">Failed to load dialing features</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => setDialingOpen(false)}
            disabled={dialingSaving}
            sx={{ color: isDark ? "rgba(255,255,255,0.7)" : undefined }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveDialing}
            variant="contained"
            disabled={dialingSaving || dialingLoading || !dialingData}
            sx={{
              bgcolor: isDark ? "#66bb6a" : "#2e7d32",
              color: "#fff",
              "&:hover": { bgcolor: isDark ? "#57a05a" : "#1b5e20" },
              "&.Mui-disabled": {
                bgcolor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
                color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.26)",
              },
            }}
          >
            {dialingSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={profileSnackbar.open} autoHideDuration={4000} onClose={() => setProfileSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={profileSnackbar.severity} onClose={() => setProfileSnackbar((s) => ({ ...s, open: false }))}>{profileSnackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default ProfilePopover;
